import { Link, useNavigate } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/platform';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { toGoogleStart } from '../api/mappers';
import { useLogin } from '../hooks/use-login';
import { useSignInProviders } from '../hooks/use-sign-in-providers';
import { loginSchema, type LoginValues } from '../schemas/login-schema';
import { isTwoFactorChallenge, type ChallengeEnd } from '../schemas/two-factor-rules';

import { GoogleSignInButton } from './google-sign-in-button';
import { SupportContact } from './support-contact';
import { TwoFactorStep } from './two-factor-step';

export interface SignInCardProps {
  /** Where to go after a successful sign-in; defaults to the console home. */
  redirectTo?: string | undefined;
}

/**
 * Email + password against POST /platform/v1/auth/login, or Google, then the two-factor code step
 * when the backend answers 202. The cookie set by login or verify is the session.
 */
export function SignInCard({ redirectTo }: SignInCardProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const login = useLogin();
  const providers = useSignInProviders();
  // Hidden while loading so they never flash; a failed lookup keeps both, the backend still
  // refuses what is switched off (Google's 409 explains itself).
  const offerGoogle = providers.data?.google ?? providers.isError;
  const offerMagicLink = providers.data?.magicLink ?? providers.isError;
  const form = useForm<LoginValues>({
    resolver: useI18nResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [challengeEnd, setChallengeEnd] = useState<ChallengeEnd | null>(null);
  const { resetField } = form;
  const { reset: resetLogin } = login;

  const enter = useCallback(
    () => navigate({ to: redirectTo ?? '/', replace: true }),
    [navigate, redirectTo],
  );
  const backToPassword = useCallback(
    (end: ChallengeEnd | null) => {
      setChallenge(null);
      setChallengeEnd(end);
      resetField('password');
      resetLogin();
    },
    [resetField, resetLogin],
  );
  const onBack = useCallback(() => backToPassword(null), [backToPassword]);

  const onSubmit = form.handleSubmit(async (values) => {
    setChallengeEnd(null);
    try {
      const result = await login.mutateAsync(values);
      if (isTwoFactorChallenge(result)) {
        setChallenge(result);
        return;
      }
      await enter();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  if (challenge) {
    return (
      <section
        aria-labelledby="platform-two-factor"
        className="bg-card text-card-foreground w-full max-w-sm rounded-2xl border p-7 shadow-[0_12px_40px_rgba(0,0,0,0.35)] sm:p-9"
      >
        <div className="mb-5 space-y-1.5">
          <h2 id="platform-two-factor" className="text-[22px] font-semibold tracking-tight">
            {t('twoFactor.title')}
          </h2>
        </div>
        <TwoFactorStep
          challenge={challenge}
          onVerified={enter}
          onBack={onBack}
          onExpired={backToPassword}
          backLabel={t('twoFactor.backToPassword')}
        />
      </section>
    );
  }

  return (
    <section
      aria-labelledby="platform-sign-in"
      className="bg-card text-card-foreground w-full max-w-sm rounded-2xl border p-7 shadow-[0_12px_40px_rgba(0,0,0,0.35)] sm:p-9"
    >
      <div className="mb-5 space-y-1.5">
        <h2 id="platform-sign-in" className="text-[22px] font-semibold tracking-tight">
          {t('login.title')}
        </h2>
        <p className="text-muted-foreground text-sm">{t('login.description')}</p>
      </div>
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {challengeEnd ? (
            <Alert role="alert">
              <AlertDescription>{t(`twoFactor.${challengeEnd}`)}</AlertDescription>
            </Alert>
          ) : null}
          {login.isError && !login.error.hasFieldErrors ? (
            <ServerError error={login.error} />
          ) : null}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t('fields.emailPlaceholder')}
                    className="h-11"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-baseline justify-between">
                  <FormLabel>{t('fields.password')}</FormLabel>
                  <Link
                    to="/forgot-password"
                    className="text-primary text-xs font-medium hover:underline"
                  >
                    {t('login.forgotPassword')}
                  </Link>
                </div>
                <FormControl>
                  <PasswordInput autoComplete="current-password" className="h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full font-semibold"
            disabled={login.isPending}
          >
            {login.isPending ? t('login.submitting') : t('login.submit')}
          </Button>
          {offerGoogle || offerMagicLink ? (
            <div className="text-muted-foreground flex items-center gap-3 text-xs" aria-hidden>
              <span className="bg-border h-px flex-1" />
              {t('login.or')}
              <span className="bg-border h-px flex-1" />
            </div>
          ) : null}
          {offerGoogle ? (
            <GoogleSignInButton getRequest={() => toGoogleStart(form.getValues('email'))} />
          ) : null}
          {offerMagicLink ? (
            <Button asChild variant="outline" size="lg" className="w-full">
              <Link to="/sign-in-link">
                <Mail aria-hidden />
                {t('login.emailLink')}
              </Link>
            </Button>
          ) : null}
          <p className="text-muted-foreground text-center text-xs leading-relaxed">
            {t('login.provisionedNote')}
          </p>
          <SupportContact variant="signIn" />
        </form>
      </Form>
    </section>
  );
}
